<template>
  <loader :is-loading="clientLoading" />
  <div class="max-w-(--breakpoint-md)">
    <lf-card v-if="!clientLoading" class="mb-8 py-6">
      <template #default>
        <loader :is-loading="isSubmitting" />
        <div>
          <access-control :roles="[SUPER_ADMIN, ADMIN]">
            <div class="border-b pt-2 pb-6">
              <lf-switch
                v-model="allowServices"
                name="data.stop_experian_dnb_services"
                @toggle-changed="handleStopExperianDnb"
              >
                <lf-h3>
                  {{ $t("ORGANIZATION.BUSINESS_CREDIT_DATA.ALLOW_APPS_DATA") }}
                </lf-h3>
                <span>
                  {{ $t("ORGANIZATION.BUSINESS_CREDIT_DATA.GIVE_ACCESS") }}
                </span>
              </lf-switch>
            </div>
          </access-control>
          <div class="mt-6">
            <div class="mb-6">
              <lf-h3>
                {{
                  isAdmin
                    ? $t("ORGANIZATION.BUSINESS_CREDIT_DATA.TITLE")
                    : $t(
                        "ORGANIZATION.BUSINESS_CREDIT_DATA.SERVICES_SUBSCRIBED"
                      )
                }}
              </lf-h3>
              <span v-if="isAdmin">
                {{
                  $t("ORGANIZATION.BUSINESS_CREDIT_DATA.CONFIGURE_ADDITIONAL")
                }}
              </span>
            </div>
            <template
              v-if="
                (isAdmin || !client.stop_experian_dnb_services) &&
                !(isFundingAdvisor || isClientFundingAdvisor)
              "
            >
              <access-control :roles="[ADMIN, SUPER_ADMIN]">
                <div
                  v-for="(
                    serviceChildren, serviceName
                  ) in businessCreditServices"
                  :key="serviceName"
                >
                  <div class="mb-2 flex">
                    <lf-checkbox
                      v-if="Object.values(serviceChildren).length === 1"
                      :id="serviceName"
                      v-model="selectedServices"
                      name="selectedServices"
                      :value="Object.keys(serviceChildren)[0]"
                    />
                    <!-- had to use custom this checbkox instead of lf one because of the styling -->
                    <input
                      v-if="Object.values(serviceChildren).length > 1"
                      :id="`${serviceName}`"
                      v-model="selectAll[serviceName]"
                      class="form-checkbox h-4 w-4 cursor-pointer rounded-xs border-gray-300 text-indigo-600"
                      type="checkbox"
                      :value="Object.keys(serviceChildren)[0]"
                      @click="
                        (e) => handleServiceSelectAll(e, `${serviceName}`)
                      "
                    />
                    <label
                      :for="`${serviceName}`"
                      class="ml-2 cursor-pointer"
                      :title="
                        $t(
                          'ORGANIZATION.BUSINESS_CREDIT_DATA.CONFIGURE_SERVICE',
                          {
                            service: serviceName,
                          }
                        )
                      "
                    >
                      {{ serviceName }}
                    </label>
                  </div>
                  <div
                    v-if="Object.values(serviceChildren).length > 1"
                    class="mt-4 flex flex-col"
                  >
                    <lf-checkbox
                      v-for="(item, itemKeyName) in serviceChildren"
                      :key="itemKeyName"
                      v-model="selectedServices"
                      class="mb-4 ml-8"
                      name="data.has_middesk"
                      :value="itemKeyName"
                    >
                      {{ item }}
                    </lf-checkbox>
                  </div>
                </div>
              </access-control>
              <access-control
                :roles="[...CLIENT_ROLE_GROUP, FUNDING_ADVISOR, UNDERWRITER]"
              >
                <template v-if="selectedServices.length">
                  <div
                    v-for="(
                      serviceChildren, serviceName
                    ) in businessCreditServices"
                    :key="serviceName"
                  >
                    <lf-h4
                      v-if="canRenderService(`${serviceName}`)"
                      class="mt-4"
                    >
                      <span class="mr-2 text-checkmark-green">
                        <icon-base class="" :icon="IconSuccessRoundBig" />
                      </span>
                      {{ serviceName }}
                    </lf-h4>
                    <div
                      v-if="Object.values(serviceChildren).length > 1"
                      class="mt-2 ml-10 flex flex-col"
                    >
                      <template
                        v-for="(child, key) in serviceChildren"
                        :key="key"
                      >
                        <span v-if="showServiceChild(key)">
                          {{ child }}
                        </span>
                      </template>
                    </div>
                  </div>
                </template>
                <div v-else>
                  <business-credit-contact />
                </div>
              </access-control>
            </template>
            <template v-else>
              <div class="my-8">
                <business-credit-contact />
              </div>
            </template>
          </div>
          <access-control :roles="[ADMIN, SUPER_ADMIN]">
            <div
              class="flex min-w-full items-center justify-end border-t pt-6 pr-5"
            >
              <primary-button
                type="button"
                :disabled="isSubmitting"
                @click="submit"
              >
                {{ $t("COMMON.SAVE_CHANGES") }}
              </primary-button>
            </div>
          </access-control>
        </div>
      </template>
    </lf-card>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import {
  ADMIN,
  CLIENT_ROLE_GROUP,
  EXPERIAN_BUSINESS_OWNER_PROFILE,
  FUNDING_ADVISOR,
  LEGACY_SERVICES,
  SUPER_ADMIN,
  UNDERWRITER,
} from "@/helpers/constants";
import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";

import LfCheckbox from "../../../components/ui/inputs/LfCheckbox.vue";
import IconSuccessRoundBig from "@/components/icons/IconSuccessRoundBig.vue";
import BusinessCreditContact from "./BusinessCreditContact.vue";

import type { IClient } from "@/models/clients";
import type {
  BusinessCreditService,
  IBusinessCreditServices,
} from "@/models/options";

type BusinessCreditServices = Omit<
  IBusinessCreditServices,
  typeof EXPERIAN_BUSINESS_OWNER_PROFILE
>;

const emit = defineEmits(["updated"]);
const { getters, dispatch } = useStore();
const { isFundingAdvisor, isAdmin, isUnderwriter, isClientFundingAdvisor } =
  useAuth();
const route = useRoute();

const client = computed<IClient>(() => ({
  ...getters[
    route.name === "ClientOrganization"
      ? "auth/authClientSettings"
      : "clients/active"
  ],
}));

const allowServices = ref(!client.value.stop_experian_dnb_services);
const selectedServices = ref(client.value.business_credit_services || []);
const selectAll = ref<Record<string, boolean>>({});

const businessCreditServices = computed<BusinessCreditServices>(() => {
  const services = getters[
    "options/businessCreditServices"
  ] as IBusinessCreditServices;
  return Object.fromEntries(
    Object.entries(services).filter(
      ([key]) => !key.includes(EXPERIAN_BUSINESS_OWNER_PROFILE)
    )
  ) as BusinessCreditServices;
});

const updateAction = computed(() =>
  route.name === "ClientOrganization"
    ? "auth/updateAuthClientSettings"
    : "clients/updateClient"
);

const clientLoading = computed(() => getters["clients/clientsLoading"]);

const getServiceValues = (serviceName: keyof BusinessCreditServices) =>
  Object.keys(
    businessCreditServices.value[serviceName]
  ) as BusinessCreditService[];

const assignServiceAllSelected = (
  serviceName: keyof BusinessCreditServices
) => {
  const serviceValues = getServiceValues(serviceName);
  selectAll.value[serviceName] = serviceValues.every((service) =>
    selectedServices.value?.includes(service)
  );
};

const handleServiceSelectAll = (
  e: MouseEvent,
  serviceName: keyof BusinessCreditServices
) => {
  const value = !!(e.target as HTMLInputElement)?.checked;
  const serviceValues = getServiceValues(serviceName);
  selectedServices.value = value
    ? [...new Set([...selectedServices.value, ...serviceValues])]
    : selectedServices.value.filter(
        (service) => !serviceValues.includes(service)
      );
};

const canRenderService = (serviceName: keyof BusinessCreditServices) =>
  Object.keys(businessCreditServices.value[serviceName]).some((service) =>
    selectedServices.value.includes(service as BusinessCreditService)
  );

const showServiceChild = (key: string | number) =>
  selectedServices.value.includes(key as BusinessCreditService);

const { loading: isSubmitting, fetchWrapper: submit } = usePromiseWrapper(
  async () => {
    if (isUnderwriter || isFundingAdvisor || isClientFundingAdvisor) {
      return;
    }
    const businessCreditServices = [...selectedServices.value].filter(
      (service) => !LEGACY_SERVICES.includes(service)
    );
    const data = {
      business_credit_services: businessCreditServices,
    };
    const response = await dispatch(
      updateAction.value,
      route.name === "ClientOrganization" ? data : { id: client.value.id, data }
    );
    if (response?.business_credit_services) {
      selectedServices.value = response.business_credit_services;
    }
    emit("updated");
  }
);

const handleStopExperianDnb = async (val: boolean) => {
  if (!isAdmin) {
    return;
  }
  const stop_experian_dnb_services = !val;
  const payload =
    route.name === "ClientOrganization"
      ? { stop_experian_dnb_services }
      : { data: { stop_experian_dnb_services } };
  await dispatch(updateAction.value, payload);
};

const runAssignment = () => {
  Object.keys(businessCreditServices.value).forEach((service) => {
    assignServiceAllSelected(service as keyof BusinessCreditServices);
  });
};

watch(selectedServices, runAssignment);

watch(
  () => client.value.business_credit_services,
  (val) => {
    selectedServices.value = val;
  }
);

Object.keys(businessCreditServices.value).forEach((service) => {
  selectAll.value[service] = false;
});
runAssignment();
</script>
